import { getQuestButtonLabel } from '@/pages/missions/questDataUtils';

const DUPLICATE_NAME_SUFFIX = ' 복사본';

export type MissionDuplicatePayload = Pick<
  Mission,
  'name' | 'isVisibleDefault' | 'isLockedDefault'
> & {
  description: string;
  order?: number;
  videoUrl?: string | null;
  videoRatioType?: Mission['videoRatioType'];
  coverImageId?: string;
  shortDescription?: string;
};

export type QuestDuplicatePayload = Pick<
  Quest,
  'name' | 'description' | 'label' | 'isVisibleDefault' | 'isLockedDefault'
> & {
  imageId?: string;
};

export interface ActionHandlerRemapOptions {
  questIdMap?: Map<number, number>;
  missionIdMap?: Map<number, number>;
}

function withDuplicateName(name: string) {
  return `${name}${DUPLICATE_NAME_SUFFIX}`;
}

export function buildMissionDuplicatePayload(
  mission: Mission,
  order?: number
): MissionDuplicatePayload {
  return {
    name: withDuplicateName(mission.name),
    description: mission.description || '<p></p>',
    shortDescription: mission.shortDescription,
    isVisibleDefault: mission.isVisibleDefault,
    isLockedDefault: mission.isLockedDefault,
    videoUrl: mission.videoUrl || null,
    videoRatioType: mission.videoRatioType,
    coverImageId: mission.coverImageId,
    order
  };
}

export function buildQuestDuplicatePayload(quest: Quest): QuestDuplicatePayload {
  return {
    name: withDuplicateName(quest.name),
    description: quest.description || '',
    label: withDuplicateName(getQuestButtonLabel(quest)),
    isVisibleDefault: quest.isVisibleDefault,
    isLockedDefault: quest.isLockedDefault,
    imageId: quest.imageId
  };
}

function remapId(value: unknown, idMap?: Map<number, number>) {
  if (!idMap) return value;

  const numericValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numericValue)) return value;

  return idMap.get(numericValue) ?? value;
}

function remapParameters(
  parameters: Record<string, unknown>,
  { questIdMap, missionIdMap }: ActionHandlerRemapOptions
) {
  return {
    ...parameters,
    ...(Object.prototype.hasOwnProperty.call(parameters, 'questId')
      ? { questId: remapId(parameters.questId, questIdMap) }
      : {}),
    ...(Object.prototype.hasOwnProperty.call(parameters, 'missionId')
      ? { missionId: remapId(parameters.missionId, missionIdMap) }
      : {})
  };
}

export function cloneActionHandlerForDuplicate(
  actionHandler: ActionHandler,
  options: ActionHandlerRemapOptions = {}
): Omit<ActionHandler, 'id'> {
  return {
    actionType: actionHandler.actionType,
    actionParameters: { ...(actionHandler.actionParameters ?? {}) },
    reactionDefinitions: (actionHandler.reactionDefinitions ?? []).map((definition) => ({
      ...definition,
      conditions: (definition.conditions ?? []).map((condition) => ({
        ...condition,
        parameters: remapParameters(condition.parameters ?? {}, options)
      })),
      commands: (definition.commands ?? []).map((command) => ({
        ...command,
        parameters: remapParameters(command.parameters ?? {}, options)
      }))
    })),
    failureCommands: (actionHandler.failureCommands ?? []).map((command) => ({
      ...command,
      parameters: remapParameters(command.parameters ?? {}, options)
    }))
  };
}
